import { Import, Settings, User, Users } from "lucide-react";
import { CanAccess, useTranslate, useUserMenu } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { ImportPage } from "../misc/ImportPage";

const Header = () => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const translate = useTranslate();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (matchPath("/tasks/*", location.pathname)) {
    currentPath = "/tasks";
  } else if (matchPath("/offer/*", location.pathname)) {
    currentPath = "/offer";
  } else if (matchPath("/channels/*", location.pathname)) {
    currentPath = "/channels";
  } else if (matchPath("/approval_queue/*", location.pathname)) {
    currentPath = "/approval_queue";
  } else if (matchPath("/agent_activity/*", location.pathname)) {
    currentPath = "/agent_activity";
  } else {
    currentPath = false;
  }

  return (
    <nav className="grow">
      <header className="sticky top-0 z-20 border-b border-border/60 bg-background/85 shadow-[0_18px_50px_-38px_rgba(15,23,42,0.55)] backdrop-blur">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <Link
              to="/"
              className="flex items-center gap-3 text-secondary-foreground no-underline"
            >
              <div className="rounded-2xl border border-primary/10 bg-primary/6 p-2 shadow-[0_10px_30px_-18px_rgba(201,151,58,0.8)]">
                <img
                  className="[.light_&]:hidden h-7"
                  src={darkModeLogo}
                  alt={title}
                />
                <img
                  className="[.dark_&]:hidden h-7"
                  src={lightModeLogo}
                  alt={title}
                />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
                  Revenue System
                </div>
                <h1 className="text-xl font-semibold text-foreground">{title}</h1>
              </div>
            </Link>

            <div className="flex items-center gap-1 rounded-full border border-border/70 bg-card/80 p-1">
              <ThemeModeToggle />
              <RefreshButton />
              <UserMenu>
                <ProfileMenu />
                <CanAccess resource="sales" action="list">
                  <UsersMenu />
                </CanAccess>
                <CanAccess resource="configuration" action="edit">
                  <SettingsMenu />
                </CanAccess>
                <ImportFromJsonMenuItem />
              </UserMenu>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <nav className="flex flex-wrap gap-2">
              <NavigationTab
                label={translate("ra.page.dashboard")}
                to="/"
                isActive={currentPath === "/"}
              />
              <NavigationTab
                label={translate("resources.contacts.name", {
                  smart_count: 2,
                })}
                to="/contacts"
                isActive={currentPath === "/contacts"}
              />
              <NavigationTab
                label={translate("resources.companies.name", {
                  smart_count: 2,
                })}
                to="/companies"
                isActive={currentPath === "/companies"}
              />
              <NavigationTab
                label={translate("resources.deals.name", {
                  smart_count: 2,
                })}
                to="/deals"
                isActive={currentPath === "/deals"}
              />
              <NavigationTab
                label={translate("resources.tasks.name", {
                  smart_count: 2,
                })}
                to="/tasks"
                isActive={currentPath === "/tasks"}
              />
              <NavigationTab
                label={translate("resources.offer.name", {
                  smart_count: 2,
                  _: "Offer",
                })}
                to="/offer"
                isActive={currentPath === "/offer"}
              />
              <NavigationTab
                label={translate("resources.channels.name", {
                  smart_count: 2,
                  _: "Channels",
                })}
                to="/channels"
                isActive={currentPath === "/channels"}
              />
              <NavigationTab
                label={translate("resources.approval_queue.name", {
                  smart_count: 2,
                  _: "Approvals",
                })}
                to="/approval_queue"
                isActive={currentPath === "/approval_queue"}
              />
              <NavigationTab
                label={translate("resources.agent_activity.name", {
                  smart_count: 2,
                  _: "Agent Activity",
                })}
                to="/agent_activity"
                isActive={currentPath === "/agent_activity"}
              />
            </nav>

            <div className="rounded-full border border-border/70 bg-card/80 px-4 py-2 text-xs uppercase tracking-[0.22em] text-muted-foreground">
              Build pipeline. Protect momentum. Close cleaner.
            </div>
          </div>
        </div>
      </header>
    </nav>
  );
};

const NavigationTab = ({
  label,
  to,
  isActive,
}: {
  label: string;
  to: string;
  isActive: boolean;
}) => (
  <Link
    to={to}
    className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
      isActive
        ? "bg-primary text-primary-foreground shadow-[0_16px_30px_-22px_rgba(201,151,58,0.95)]"
        : "bg-secondary/70 text-secondary-foreground/75 hover:text-secondary-foreground"
    }`}
  >
    {label}
  </Link>
);

const UsersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<UsersMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};
export default Header;
